import React, { useState, useRef, useEffect, useCallback } from "react"
import { graphql, Link } from "gatsby"
import { Helmet } from "react-helmet"
import Layout from "../components/layout"

import {
  shortsPage,
  shortsContainer,
  shortWrapper,
  videoWrapper,
  videoFrame,
  videoOverlay,
  navigationArrows,
  navArrow,
  videoCounter,
  homeButton,
  logo,
  loadingWrapper,
  swipeHint,
  muteButton,
} from "./shorts.module.css"

// Extract YouTube video ID from various URL formats
function extractYouTubeID(url) {
  if (!url) return null
  const regExp =
    /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?|shorts\/)\??v?=?([^#&?]*).*/
  const match = url.match(regExp)
  if (match && match[2].length === 11) {
    return match[2]
  }
  return null
}

// Filter videos to only include YouTube shorts
const filterYouTubeVideos = (videos) => {
  return videos
    .filter((video) => {
      const id = extractYouTubeID(video.url)
      return id !== null
    })
    .map((video) => ({
      ...video,
      youtubeId: extractYouTubeID(video.url),
    }))
}

// Simple YouTube iframe component
const YouTubeShort = ({ videoId, isActive, isPreload, index, isMuted }) => {
  const [hasBeenActive, setHasBeenActive] = useState(isActive)
  const [loadedMuteState, setLoadedMuteState] = useState(isMuted)

  // Once a video becomes active, keep it loaded
  useEffect(() => {
    if (isActive && !hasBeenActive) {
      setHasBeenActive(true)
    }
  }, [isActive, hasBeenActive])

  // Only update mute state and reload when this video is active
  useEffect(() => {
    if (isActive) {
      setLoadedMuteState(isMuted)
    }
  }, [isActive, isMuted])

  const shouldRender = isPreload || hasBeenActive

  if (!shouldRender) {
    return (
      <div style={{ 
        width: '100%', 
        height: '100%', 
        background: '#1a1a1a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666'
      }}>
        <span>Laden...</span>
      </div>
    )
  }

  // For active video: use current mute state
  // For inactive videos: always muted, no autoplay
  const isActiveVideo = isActive
  const effectiveMuted = isActiveVideo ? loadedMuteState : true
  const effectiveAutoplay = isActiveVideo ? 1 : 0

  const src = `https://www.youtube.com/embed/${videoId}?autoplay=${effectiveAutoplay}&mute=${effectiveMuted ? 1 : 0}&loop=1&playlist=${videoId}&controls=0&modestbranding=1&rel=0&playsinline=1&showinfo=0&iv_load_policy=3&disablekb=1`

  // Key only changes for active video when mute changes, or when becoming active
  const iframeKey = isActiveVideo 
    ? `active-${loadedMuteState ? 'muted' : 'unmuted'}-${videoId}` 
    : `inactive-${videoId}`

  return (
    <iframe
      key={iframeKey}
      style={{ width: '100%', height: '100%', border: 'none' }}
      src={src}
      title={`Video ${index + 1}`}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  )
}

const ShortsPage = ({ data }) => {
  const allVideos = data?.allContentfulVideo?.nodes || []
  const videos = filterYouTubeVideos(allVideos)
  
  // Debug logging
  useEffect(() => {
    console.log('All videos from Contentful:', allVideos)
    console.log('Filtered YouTube videos:', videos)
  }, [allVideos, videos])
  
  const containerRef = useRef(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [showHint, setShowHint] = useState(true)
  const [isMuted, setIsMuted] = useState(true) // Start muted for autoplay to work

  // Handle scroll to update current index
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return
    const container = containerRef.current
    const scrollPosition = container.scrollTop
    const itemHeight = container.clientHeight
    const newIndex = Math.round(scrollPosition / itemHeight) % videos.length
    setCurrentIndex(newIndex >= 0 ? newIndex : 0)
  }, [videos.length])

  useEffect(() => {
    const container = containerRef.current
    if (container) {
      container.addEventListener("scroll", handleScroll)
      return () => container.removeEventListener("scroll", handleScroll)
    }
  }, [handleScroll])

  // Hide hint after first interaction
  useEffect(() => {
    const hideHint = () => setShowHint(false)
    window.addEventListener("touchstart", hideHint, { once: true })
    window.addEventListener("wheel", hideHint, { once: true })
    return () => {
      window.removeEventListener("touchstart", hideHint)
      window.removeEventListener("wheel", hideHint)
    }
  }, [])

  // Navigate to specific video
  const scrollToIndex = useCallback((index) => {
    if (!containerRef.current || videos.length === 0) return
    
    // Handle wrapping for infinite scroll
    let targetIndex = index
    if (index < 0) {
      targetIndex = videos.length - 1
    } else if (index >= videos.length) {
      targetIndex = 0
    }
    
    const container = containerRef.current
    const itemHeight = container.clientHeight
    container.scrollTo({
      top: targetIndex * itemHeight,
      behavior: "smooth",
    })
    setCurrentIndex(targetIndex)
  }, [videos.length])

  // Navigation functions
  const goToPrevious = useCallback(() => {
    scrollToIndex(currentIndex - 1)
  }, [currentIndex, scrollToIndex])

  const goToNext = useCallback(() => {
    scrollToIndex(currentIndex + 1)
  }, [currentIndex, scrollToIndex])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault()
        goToPrevious()
      } else if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault()
        goToNext()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [goToPrevious, goToNext])

  // Handle scroll end for infinite loop
  useEffect(() => {
    const container = containerRef.current
    if (!container || videos.length === 0) return

    let scrollTimeout
    const handleScrollEnd = () => {
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => {
        const scrollPosition = container.scrollTop
        const itemHeight = container.clientHeight
        const totalHeight = itemHeight * videos.length
        
        // If scrolled past the last item, reset to first
        if (scrollPosition >= totalHeight - 10) {
          container.scrollTo({ top: 0, behavior: "auto" })
          setCurrentIndex(0)
        }
      }, 150)
    }

    container.addEventListener("scroll", handleScrollEnd)
    return () => {
      container.removeEventListener("scroll", handleScrollEnd)
      clearTimeout(scrollTimeout)
    }
  }, [videos.length])

  if (videos.length === 0) {
    return (
      <Layout>
        <Helmet>
          <title>Shorts | MKB Servicedesk</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className={loadingWrapper}>
          <p>Geen video's beschikbaar</p>
          <p style={{ fontSize: '12px', marginTop: '10px', color: '#888' }}>
            {allVideos.length} video's gevonden, {videos.length} YouTube video's
          </p>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <Helmet>
        <title>Shorts | MKB Servicedesk</title>
        <meta name="description" content="Bekijk korte video's van MKB Servicedesk" />
        <meta name="theme-color" content="#000000" />
      </Helmet>

      <div className={shortsPage}>
        {/* Home button */}
        <Link to="/" className={homeButton} aria-label="Terug naar home">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Link>

        {/* Logo */}
        <Link to="/" className={logo} aria-label="MKB Servicedesk">
          <img src="/icons/icon-256x256.png" alt="MKB Servicedesk Logo" />
        </Link>

        {/* Mute toggle button */}
        <button 
          className={muteButton} 
          onClick={() => setIsMuted(!isMuted)}
          aria-label={isMuted ? "Geluid aan" : "Geluid uit"}
        >
          {isMuted ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="23" y1="9" x2="17" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="17" y1="9" x2="23" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M15.54 8.46C16.4774 9.39764 17.0039 10.6692 17.0039 11.995C17.0039 13.3208 16.4774 14.5924 15.54 15.53" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M19.07 4.93C20.9447 6.80528 21.9979 9.34836 21.9979 12C21.9979 14.6516 20.9447 17.1947 19.07 19.07" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </button>

        {/* Shorts container */}
        <div className={shortsContainer} ref={containerRef}>
          {videos.map((video, index) => {
            // Preload more videos for instant playback (2 before and 2 after)
            const distance = Math.min(
              Math.abs(index - currentIndex),
              Math.abs(index - currentIndex + videos.length),
              Math.abs(index - currentIndex - videos.length)
            )
            const shouldPreload = distance <= 2
            const isCurrentVideo = index === currentIndex
            
            return (
              <div key={video.contentful_id} className={shortWrapper}>
                <div className={videoWrapper}>
                  <YouTubeShort
                    videoId={video.youtubeId}
                    isActive={isCurrentVideo}
                    isPreload={shouldPreload}
                    index={index}
                    isMuted={isMuted}
                  />
                  {/* Clickable overlay for navigation */}
                  <Link to="/" className={videoOverlay} aria-label="Ga naar artikel">
                    <span style={{ opacity: 0 }}>Klik om naar home te gaan</span>
                  </Link>
                </div>
              </div>
            )
          })}
        </div>

        {/* Desktop navigation arrows */}
        <div className={navigationArrows}>
          <button
            className={navArrow}
            onClick={goToPrevious}
            aria-label="Vorige video"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 15L12 9L6 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button
            className={navArrow}
            onClick={goToNext}
            aria-label="Volgende video"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Mobile swipe hint */}
        {showHint && (
          <div className={swipeHint}>
            ↕ Swipe om te navigeren
          </div>
        )}
      </div>
    </Layout>
  )
}

export default ShortsPage

export const pageQuery = graphql`
  query ShortsPageQuery {
    allContentfulVideo {
      nodes {
        contentful_id
        url
      }
    }
  }
`
